import { Add, Close } from '@mui/icons-material';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { StatusBadge } from '@/tracker/entities/status';
import { CreatableTaskLinkRelationship, Task } from '@/tracker/types';

import { CardSection } from '../CardSection';
import { TaskPicker } from '../TaskPicker';
import { IssueCardStore } from '../../model/IssueCardStore';
import { useIssueDrawer } from '../../useIssueDrawer';

import { CREATABLE_LINKS, groupLinks, LINK_LABELS } from './config';

import s from './IssueLinks.module.scss';

type Props = {
  issue: Task;
  store: IssueCardStore;
};

/** Связи задачи по типам: «Зависит от», «Блокирует»… Добавление — тип, затем задача; удаление по наведению */
const IssueLinks: React.FC<Props> = observer(({ issue, store }) => {
  const { issueLink } = useIssueDrawer();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [adding, setAdding] = useState<CreatableTaskLinkRelationship | null>(null);
  // Тип из меню: поле задачи появляется, только когда меню закрылось целиком
  const [chosen, setChosen] = useState<CreatableTaskLinkRelationship | null>(null);

  return (
    <CardSection title="Связи" count={issue.links.length}>
      {groupLinks(issue.links).map(({ relationship, links }) => (
        <div key={relationship} className={s.group}>
          <h4 className={s.group__title}>{LINK_LABELS[relationship]}</h4>
          <ul className={s.list}>
            {links.map(({ id, task }) => (
              <li key={id} className={s.list__item}>
                {/* Задача из закрытой очереди: известен только ключ, карточка не откроется */}
                {task.summary === null ? (
                  <span className={s.list__key}>{task.key}</span>
                ) : (
                  <Link to={issueLink(task.key)} className={s.list__link}>
                    {task.key}
                  </Link>
                )}
                <span className={s.list__summary}>{task.summary ?? 'Нет доступа к задаче'}</span>
                {task.status && <StatusBadge status={task.status} />}
                {!store.isReadOnly && (
                  <IconButton
                    size="small"
                    className={s.list__remove}
                    aria-label={`Удалить связь с ${task.key}`}
                    disabled={store.isSaving}
                    onClick={() => store.removeLink(id)}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {adding && (
        <div className={s.picker}>
          <span className={s.picker__label}>{LINK_LABELS[adding]}</span>
          <div className={s.picker__field}>
            <TaskPicker
              search={store.searchLinkCandidates}
              ariaLabel={`Связь: ${LINK_LABELS[adding].toLowerCase()}`}
              onSave={(key) => key && store.addLink({ relationship: adding, task: key })}
              onClose={() => setAdding(null)}
            />
          </div>
        </div>
      )}
      {!adding && !store.isSaving && !store.isReadOnly && (
        <button
          type="button"
          className={s.add}
          aria-haspopup="menu"
          onClick={(event) => setMenuAnchor(event.currentTarget)}
        >
          <Add fontSize="inherit" />
          Добавить связь
        </button>
      )}
      {/* Пока меню закрывается, оно держит фокус у себя — поле с autoFocus потеряло бы его и закрылось */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        disableRestoreFocus
        onClose={() => setMenuAnchor(null)}
        slotProps={{
          transition: {
            onExited: () => {
              setAdding(chosen);
              setChosen(null);
            },
          },
        }}
      >
        {CREATABLE_LINKS.map((relationship) => (
          <MenuItem
            key={relationship}
            dense
            onClick={() => {
              setMenuAnchor(null);
              setChosen(relationship);
            }}
          >
            {LINK_LABELS[relationship]}
          </MenuItem>
        ))}
      </Menu>
    </CardSection>
  );
});

export { IssueLinks };
